import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { livestockById } from "../lib/constants";
import { StatCard } from "../components/StatCard";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Wallet, TrendingUp, TrendingDown, PiggyBank, Clock, ReceiptText, Percent, Landmark, AlertTriangle, Layers,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";

export default function Dashboard() {
  const { fmt, settings, alertCount } = useFarm();
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [herd, setHerd] = useState([]);
  const active = livestockById(settings.active_livestock);
  const headcount = herd.filter((h) => h.livestock_type === settings.active_livestock && h.status === "active").reduce((s, h) => s + (h.count || 0), 0);

  useEffect(() => {
    api.summary().then(setSummary);
    api.monthly().then(setMonthly);
    api.pnl().then(setPnl);
    api.getHerd().then(setHerd);
  }, []);

  const chartMonths = monthly.map((m) => ({
    ...m,
    label: new Date(m.month + "-01").toLocaleDateString(undefined, { month: "short" }),
  }));

  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-2xl border border-border">
        <img src={active.image} alt={active.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/30" />
        <div className="relative p-6 sm:p-10">
          <Badge className="bg-accent text-accent-foreground hover:bg-accent border-0 mb-3">
            {active.name} · {active.subtitle}
          </Badge>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-primary-foreground">
            {settings.farm_name}
          </h1>
          <p className="mt-2 max-w-xl text-primary-foreground/80 text-sm sm:text-base">
            Track every animal, activity, and coin. Real-time finances for your livestock operation.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {active.metrics.map((m) => (
              <span key={m} className="rounded-full bg-primary-foreground/12 px-3 py-1 text-xs text-primary-foreground/90 backdrop-blur">
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {alertCount > 0 && (
        <Link to="/monthly" data-testid="dashboard-alert-banner" className="flex items-center gap-3 rounded-xl border border-destructive/50 bg-destructive/10 px-4 py-3 text-destructive transition-colors hover:bg-destructive/15">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-destructive text-destructive-foreground">
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{alertCount} irregularity {alertCount === 1 ? "alert" : "alerts"} detected</p>
            <p className="text-xs opacity-80">Unusual changes in expenses, income or produce — tap to review the Monthly report.</p>
          </div>
          <span className="grid min-w-[24px] h-6 place-items-center rounded-full bg-destructive px-1.5 text-xs font-bold text-destructive-foreground">{alertCount}</span>
        </Link>
      )}

      {summary && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="dashboard-headcount-card" label={`Total ${active.register}`} value={headcount.toLocaleString()} sub={active.name} icon={Layers} tone="accent" delay={0} />
          <StatCard testid="financial-summary-card-revenue" label="Total Revenue" value={fmt(summary.revenue)} sub="Income received" icon={TrendingUp} tone="emerald" delay={0} />
          <StatCard testid="financial-summary-card-expenses" label="Total Expenses" value={fmt(summary.expenses)} sub="Costs paid" icon={TrendingDown} tone="terracotta" delay={60} />
          <StatCard testid="financial-summary-card-profit" label="Net Profit" value={fmt(summary.net_profit)} sub={`${summary.profit_margin}% margin`} icon={PiggyBank} tone="primary" delay={120} />
          <StatCard testid="financial-summary-card-cash" label="Cash Balance" value={fmt(summary.cash_balance)} sub="Available now" icon={Wallet} tone="accent" delay={180} />
          <StatCard testid="financial-summary-card-expected" label="Expected Income" value={fmt(summary.expected_income)} sub="Receivables pending" icon={Clock} tone="emerald" delay={240} />
          <StatCard testid="financial-summary-card-upcoming" label="Upcoming Expenses" value={fmt(summary.upcoming_expenses)} sub="Scheduled outgoing" icon={Landmark} tone="terracotta" delay={300} />
          <StatCard testid="financial-summary-card-bills" label="Outstanding Bills" value={fmt(summary.outstanding_bills)} sub="Unpaid payable" icon={ReceiptText} tone="destructive" delay={360} />
          <StatCard testid="financial-summary-card-margin" label="Profit Margin" value={`${summary.profit_margin}%`} sub={`${summary.transaction_count} transactions`} icon={Percent} tone="primary" delay={420} />
        </section>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6" data-testid="cashflow-chart">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl font-bold tracking-tight">Cash Flow Trend</h2>
            <span className="text-xs font-mono-nums uppercase tracking-widest text-muted-foreground">Monthly</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartMonths} margin={{ left: -12, right: 8 }}>
              <defs>
                <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--emerald))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--emerald))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--terracotta))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--terracotta))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={54} />
              <Tooltip
                formatter={(v, n) => [fmt(v), n === "income" ? "Income" : "Expense"]}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 13 }}
              />
              <Area type="monotone" dataKey="income" stroke="hsl(var(--emerald))" strokeWidth={2} fill="url(#inc)" />
              <Area type="monotone" dataKey="expense" stroke="hsl(var(--terracotta))" strokeWidth={2} fill="url(#exp)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6" data-testid="expense-breakdown-chart">
          <h2 className="font-display text-xl font-bold tracking-tight mb-6">Top Expenses</h2>
          {pnl && pnl.expenses.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={pnl.expenses.slice(0, 6)} layout="vertical" margin={{ left: 8, right: 16 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="category" width={92} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 13 }} />
                <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                  {pnl.expenses.slice(0, 6).map((_, i) => (
                    <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-12 text-center">No expenses recorded yet.</p>
          )}
        </Card>
      </section>
    </div>
  );
}
